import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { Pencil, Building2, ChevronDown } from "lucide-react";
import { NoAccess } from "@/components/AppShell";
import { PhoneInput } from "@/components/PhoneInput";
import { useAuth } from "@/context/AuthContext";
import { inr } from "@/lib/format";
import { firstError, splitE164, validateEmail, validatePhone, validateRegistrationNo } from "@/lib/validate";

const EMPTY_COMP = { model: "fixed_consult", salary_amount: "", fixed_amount: "", percentage: "" };
const EMPTY_DOC = { name: "", qualification: "", specialization: "", department_id: "", phone: "",
  email: "", consultation_fee: "", follow_up_fee: "", emergency_fee: "", registration_no: "",
  schedule: "", compensation: { ...EMPTY_COMP } };

const validateDoctor = (d) => {
  const e = {};
  if (!d.name.trim()) e.name = "Doctor name is required.";
  if (!d.specialization.trim()) e.specialization = "Specialisation is required.";
  if (!d.department_id) e.department_id = "Department is required.";
  const reg = validateRegistrationNo(d.registration_no, "Registration number");
  if (reg) e.registration_no = reg;
  const { country, national } = splitE164(d.phone);
  const p = validatePhone(country, national, { required: true, label: "Mobile number" });
  if (p) e.phone = p;
  const em = validateEmail(d.email);
  if (em) e.email = em;
  if (!Number(d.consultation_fee)) e.consultation_fee = "Consultation fee is required.";
  if (!d.schedule.trim()) e.schedule = "Working schedule is required.";
  const c = d.compensation || {};
  if (c.model === "salary" && !Number(c.salary_amount)) e.compensation = "Enter the monthly salary for a salary-based doctor.";
  if (c.model === "fixed_consult" && !Number(c.fixed_amount)) e.compensation = "Enter the amount retained per consultation for a consultant.";
  if (c.model === "percentage" && !(Number(c.percentage) > 0 && Number(c.percentage) <= 100)) e.compensation = "Enter a percentage between 0 and 100.";
  return e;
};

const buildCompensation = (c) => {
  if (!c || !c.model || c.model === "none") return null;
  if (c.model === "salary") return { model: "salary", salary_amount: Number(c.salary_amount || 0) };
  if (c.model === "fixed_consult") return { model: "fixed_consult", fixed_amount: Number(c.fixed_amount || 0) };
  if (c.model === "percentage") return { model: "percentage", percentage: Number(c.percentage || 0) };
  return null;
};

const compLabel = (c) => {
  if (!c || !c.model) return "—";
  if (c.model === "salary") return `Salary · ${inr(c.salary_amount || 0)}/mo`;
  if (c.model === "fixed_consult") return `Consultant · ${inr(c.fixed_amount || 0)}/consult`;
  if (c.model === "percentage") return `Percentage · ${c.percentage || 0}%`;
  return "—";
};

export default function DoctorsDepartments() {
  const { can } = useAuth();
  const [doctors, setDoctors] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [error, setError] = useState("");
  const [docOpen, setDocOpen] = useState(false);
  const [deptOpen, setDeptOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [doc, setDoc] = useState(EMPTY_DOC);
  const [errors, setErrors] = useState({});
  const [dept, setDept] = useState({ name: "", description: "" });
  const [formError, setFormError] = useState("");
  const [showDepts, setShowDepts] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, dep] = await Promise.all([api.get("/hospital/doctors"), api.get("/hospital/departments")]);
      setDoctors(d.data.items);
      setDepartments(dep.data.items);
    } catch (e) { setError(apiError(e)); }
  }, []);

  useEffect(() => { if (can("hospital.dashboard")) load(); }, [load, can]);
  if (!can("hospital.dashboard")) return <NoAccess what="doctors and departments" />;

  const setComp = (patch) => setDoc((prev) => ({ ...prev, compensation: { ...prev.compensation, ...patch } }));

  const saveDoctor = async (e) => {
    e.preventDefault();
    const v = validateDoctor(doc);
    setErrors(v);
    if (Object.keys(v).length) { setFormError(firstError(v)); return; }
    setFormError("");
    const payload = { name: doc.name, qualification: doc.qualification, specialization: doc.specialization,
      department_id: doc.department_id, phone: doc.phone, email: doc.email, registration_no: doc.registration_no,
      schedule: doc.schedule,
      consultation_fee: Number(doc.consultation_fee || 0),
      follow_up_fee: Number(doc.follow_up_fee || 0),
      emergency_fee: Number(doc.emergency_fee || 0),
      compensation: buildCompensation(doc.compensation) };
    try {
      if (editing) await api.patch(`/hospital/doctors/${editing}`, payload);
      else await api.post("/hospital/doctors", payload);
      toast.success(editing ? "Doctor updated." : "Doctor added.");
      setDocOpen(false); setEditing(null); setDoc(EMPTY_DOC); setErrors({}); load();
    } catch (err) { setFormError(apiError(err)); }
  };

  const openEdit = (d) => {
    setEditing(d.doctor_id); setErrors({});
    setDoc({ ...EMPTY_DOC, ...d, consultation_fee: d.consultation_fee || "",
      follow_up_fee: d.follow_up_fee || "", emergency_fee: d.emergency_fee || "",
      compensation: { ...EMPTY_COMP, ...(d.compensation || {}),
        model: d.compensation?.model || "fixed_consult",
        salary_amount: d.compensation?.salary_amount || "",
        fixed_amount: d.compensation?.fixed_amount || "",
        percentage: d.compensation?.percentage || "" } });
    setDocOpen(true);
  };

  const saveDept = async (e) => {
    e.preventDefault(); setFormError("");
    if (!dept.name.trim()) { setFormError("Department name is required."); return; }
    try {
      await api.post("/hospital/departments", dept);
      toast.success("Department added.");
      setDeptOpen(false); setDept({ name: "", description: "" }); load();
    } catch (err) { setFormError(apiError(err)); }
  };

  if (error) return <ErrorState message={error} />;
  if (!doctors) return <Loading />;

  const Err = ({ msg }) => msg ? <p className="mt-1.5 text-xs font-medium text-red-600">{msg}</p> : null;

  return (
    <div data-testid="doctors-page">
      <PageHeader overline="Hospital" title="Doctors & departments"
        subtitle="Consultation fees and the compensation model are configured together when you add a doctor — no separate step."
        actions={(can("hospital.departments.manage") || can("hospital.doctors.manage")) && (
          <>
            <button onClick={() => setShowDepts((v) => !v)} className={ghostBtnCls} data-testid="toggle-departments-btn">
              <Building2 className="h-4 w-4" strokeWidth={1.8} /> Departments ({departments.length})
              <ChevronDown className={`h-4 w-4 transition-transform ${showDepts ? "rotate-180" : ""}`} />
            </button>
            {can("hospital.departments.manage") && (
              <button onClick={() => setDeptOpen(true)} className={ghostBtnCls} data-testid="add-department-btn">Add department</button>
            )}
            {can("hospital.doctors.manage") && (
              <button onClick={() => { setEditing(null); setDoc(EMPTY_DOC); setErrors({}); setDocOpen(true); }}
                className={primaryBtnCls} data-testid="add-doctor-btn">Add doctor</button>
            )}
          </>
        )} />

      {showDepts && (
        <div className="panel mb-6" data-testid="departments-panel">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <h3 className="font-ui font-bold tracking-tight">Departments</h3>
            <span className="text-xs text-slate-400">{departments.length} total</span>
          </div>
          {departments.length === 0 ? (
            <EmptyState title="No departments" subtitle="Create a department before adding doctors." testid="departments-empty" />
          ) : (
            <ul className="grid gap-px bg-slate-100 sm:grid-cols-2 lg:grid-cols-3" data-testid="departments-list">
              {departments.map((d) => (
                <li key={d.department_id} className="bg-white px-5 py-3.5">
                  <p className="text-sm font-medium text-slate-900">{d.name}</p>
                  {d.description && <p className="text-xs text-slate-500 mt-1">{d.description}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <Table testid="doctors-table" columns={["Doctor", "Specialisation", "Department", "Consultation", "Compensation", "Mobile", "Actions"]}
        rows={doctors} empty={<EmptyState title="No doctors added" subtitle="Add doctors to enable appointments, OPD and consultation billing." testid="doctors-empty" />}
        renderRow={(d) => (
          <tr key={d.doctor_id} data-testid={`doctor-row-${d.doctor_id}`}>
            <Td className="font-medium text-slate-900">Dr. {d.name}<span className="block text-xs text-slate-400">{d.qualification || d.registration_no}</span></Td>
            <Td className="text-slate-600">{d.specialization || "—"}</Td>
            <Td className="text-slate-600">{departments.find((x) => x.department_id === d.department_id)?.name || "—"}</Td>
            <Td className={d.consultation_fee ? "text-slate-800" : "text-red-600"}>
              {d.consultation_fee ? inr(d.consultation_fee) : "Not configured"}
            </Td>
            <Td className="text-slate-600" data-testid={`doctor-comp-${d.doctor_id}`}>{compLabel(d.compensation)}</Td>
            <Td className="text-slate-600">{d.phone || "—"}</Td>
            <Td>
              <div className="flex items-center gap-1.5">
                {can("hospital.doctors.manage") && (
                  <button onClick={() => openEdit(d)} title="Edit" aria-label="Edit"
                    data-testid={`edit-doctor-${d.doctor_id}`}
                    className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 bg-white text-slate-600 hover:border-[#EA580C] hover:text-[#EA580C]">
                    <Pencil className="h-4 w-4" strokeWidth={1.8} />
                  </button>
                )}
              </div>
            </Td>
          </tr>
        )} />

      <Modal open={docOpen} onClose={() => setDocOpen(false)} wide
        title={editing ? "Edit doctor" : "Add doctor"} testid="add-doctor-modal"
        subtitle="Fields marked * are mandatory and validated on the server.">
        <form onSubmit={saveDoctor} className="grid gap-5 sm:grid-cols-2">
          <Field label="Doctor name *">
            <input className={inputCls} data-testid="doctor-name-input" value={doc.name}
              onChange={(e) => setDoc({ ...doc, name: e.target.value })} />
            <Err msg={errors.name} />
          </Field>
          <Field label="Qualification">
            <input className={inputCls} value={doc.qualification} placeholder="MBBS, MD"
              onChange={(e) => setDoc({ ...doc, qualification: e.target.value })} />
          </Field>
          <Field label="Specialisation *">
            <input className={inputCls} data-testid="doctor-specialization-input" value={doc.specialization}
              onChange={(e) => setDoc({ ...doc, specialization: e.target.value })} />
            <Err msg={errors.specialization} />
          </Field>
          <Field label="Department *">
            <select className={inputCls} value={doc.department_id} data-testid="doctor-department-select"
              onChange={(e) => setDoc({ ...doc, department_id: e.target.value })}>
              <option value="">Select department</option>
              {departments.map((d) => <option key={d.department_id} value={d.department_id}>{d.name}</option>)}
            </select>
            <Err msg={errors.department_id} />
          </Field>
          <Field label="Registration number *">
            <input className={inputCls} data-testid="doctor-registration-input" value={doc.registration_no}
              onChange={(e) => setDoc({ ...doc, registration_no: e.target.value })} />
            <Err msg={errors.registration_no} />
          </Field>
          <PhoneInput label="Mobile" required value={doc.phone} testid="doctor-phone"
            error={errors.phone} onChange={(v) => setDoc({ ...doc, phone: v })} />
          <Field label="Email">
            <input className={inputCls} data-testid="doctor-email-input" value={doc.email}
              onChange={(e) => setDoc({ ...doc, email: e.target.value })} />
            <Err msg={errors.email} />
          </Field>
          <Field label="Working schedule *">
            <input className={inputCls} data-testid="doctor-schedule-input" value={doc.schedule}
              placeholder="Mon–Sat, 10:00–14:00 & 17:00–20:00"
              onChange={(e) => setDoc({ ...doc, schedule: e.target.value })} />
            <Err msg={errors.schedule} />
          </Field>
          <Field label="Consultation fee (₹) *">
            <input type="number" min="1" className={inputCls} data-testid="doctor-fee-input" value={doc.consultation_fee}
              onChange={(e) => setDoc({ ...doc, consultation_fee: e.target.value })} />
            <Err msg={errors.consultation_fee} />
          </Field>
          <Field label="Follow-up fee (₹)">
            <input type="number" className={inputCls} data-testid="doctor-followup-fee-input" value={doc.follow_up_fee}
              onChange={(e) => setDoc({ ...doc, follow_up_fee: e.target.value })} />
          </Field>
          <Field label="Emergency fee (₹)">
            <input type="number" className={inputCls} data-testid="doctor-emergency-fee-input" value={doc.emergency_fee}
              onChange={(e) => setDoc({ ...doc, emergency_fee: e.target.value })} />
          </Field>

          <div className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-4">
            <p className="overline text-[#EA580C]">Compensation</p>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Compensation type *">
                <select className={inputCls} data-testid="doctor-comp-model" value={doc.compensation.model}
                  onChange={(e) => setComp({ model: e.target.value })}>
                  <option value="fixed_consult">Consultant (fee retained per consultation)</option>
                  <option value="salary">Salary-based</option>
                  <option value="percentage">Percentage-based</option>
                  <option value="none">Not configured</option>
                </select>
              </Field>
              {doc.compensation.model === "fixed_consult" && (
                <Field label="Amount retained per consultation (₹) *">
                  <input type="number" className={inputCls} data-testid="doctor-comp-fixed" value={doc.compensation.fixed_amount}
                    onChange={(e) => setComp({ fixed_amount: e.target.value })} />
                </Field>
              )}
              {doc.compensation.model === "salary" && (
                <Field label="Monthly salary (₹) *">
                  <input type="number" className={inputCls} data-testid="doctor-comp-salary" value={doc.compensation.salary_amount}
                    onChange={(e) => setComp({ salary_amount: e.target.value })} />
                </Field>
              )}
              {doc.compensation.model === "percentage" && (
                <Field label="Percentage of consultation fee (%) *">
                  <input type="number" className={inputCls} data-testid="doctor-comp-percentage" value={doc.compensation.percentage}
                    onChange={(e) => setComp({ percentage: e.target.value })} />
                </Field>
              )}
            </div>
            <p className="text-xs text-slate-500">Percentage / consultant earnings are calculated on the consultation fee only — never on lab or pharmacy charges. Salary doctors accrue via payroll.</p>
            <Err msg={errors.compensation} />
          </div>

          {formError && <p className="sm:col-span-2 text-sm font-medium text-red-600" data-testid="doctor-form-error">{formError}</p>}
          <div className="sm:col-span-2 flex gap-3">
            <button className={primaryBtnCls} data-testid="doctor-submit-btn">{editing ? "Save changes" : "Save doctor"}</button>
            <button type="button" onClick={() => setDocOpen(false)} className={ghostBtnCls}>Cancel</button>
          </div>
        </form>
      </Modal>

      <Modal open={deptOpen} onClose={() => setDeptOpen(false)} title="Add department" testid="add-department-modal">
        <form onSubmit={saveDept} className="space-y-5">
          <Field label="Department name *"><input data-testid="department-name-input" className={inputCls} value={dept.name} onChange={(e) => setDept({ ...dept, name: e.target.value })} /></Field>
          <Field label="Description"><input className={inputCls} value={dept.description} onChange={(e) => setDept({ ...dept, description: e.target.value })} /></Field>
          {formError && <p className="text-sm font-medium text-red-600">{formError}</p>}
          <button className={primaryBtnCls} data-testid="department-submit-btn">Save department</button>
        </form>
      </Modal>
    </div>
  );
}
